import { useEffect, useState, useCallback } from 'react'
import {
  TrendingUp, RefreshCw, Search, CheckCircle2, XCircle,
  Clock, ShieldCheck, BarChart3, AlertTriangle,
} from 'lucide-react'
import styles from './RPORTOPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface RPORTOActual {
  id: number
  drill_id: number
  drill_title: string
  product_name: string
  target_rpo_hours: number
  target_rto_hours: number
  actual_rpo_hours: number
  actual_rto_hours: number
  rpo_met: boolean
  rto_met: boolean
  recorded_at: string
}

interface Product {
  id: number
  product_name: string
  rpo_target_hours: string
  rto_target_hours: string
  criticality: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function fmtHours(h: number): string {
  if (h == null) return '—'
  return `${Number(h).toFixed(1)}h`
}

function MetBadge({ met }: { met: boolean }) {
  return met ? (
    <span className={styles.badgeGreen}>
      <CheckCircle2 size={12} /> Met
    </span>
  ) : (
    <span className={styles.badgeRed}>
      <XCircle size={12} /> Missed
    </span>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
  variant = 'neutral',
}: {
  icon: React.ElementType
  label: string
  value: string | number
  sub?: string
  variant?: 'neutral' | 'ok' | 'warn' | 'bad'
}) {
  return (
    <div className={`${styles.summaryCard} ${styles[`summaryCard_${variant}`]}`}>
      <div className={styles.summaryIcon}><Icon size={18} /></div>
      <div>
        <div className={styles.summaryValue}>{value}</div>
        <div className={styles.summaryLabel}>{label}</div>
        {sub && <div className={styles.summarySub}>{sub}</div>}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RPORTOPage() {
  const [records, setRecords] = useState<RPORTOActual[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [rRes, pRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/rpo-rto/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/products/?page_size=100`, { headers: authHeaders() }),
      ])
      if (!rRes.ok || !pRes.ok) throw new Error('API error')
      const [rData, pData] = await Promise.all([rRes.json(), pRes.json()])
      setRecords(rData.results ?? rData)
      setProducts(pData.results ?? [])
    } catch {
      setError('Cannot reach DR API. Make sure the dr-api service is running on port 8001.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ── Derived stats ──────────────────────────────────────────────────────────

  const totalDrills = records.length
  const rpoCompliant = records.filter(r => r.rpo_met).length
  const rtoCompliant = records.filter(r => r.rto_met).length
  const bothCompliant = records.filter(r => r.rpo_met && r.rto_met).length

  const rpcPct = totalDrills > 0 ? Math.round((rpoCompliant / totalDrills) * 100) : 0
  const rtcPct = totalDrills > 0 ? Math.round((rtoCompliant / totalDrills) * 100) : 0
  const bothPct = totalDrills > 0 ? Math.round((bothCompliant / totalDrills) * 100) : 0

  // ── Filter ────────────────────────────────────────────────────────────────

  const filtered = records.filter(r => {
    if (!search) return true
    const q = search.toLowerCase()
    return r.product_name.toLowerCase().includes(q) || r.drill_title.toLowerCase().includes(q)
  })

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <TrendingUp size={12} />
            <span>Disaster Recovery</span>
            <span className={styles.breadSep}>/</span>
            <span>RPO / RTO Compliance</span>
          </div>
          <h1 className={styles.heroTitle}>RPO / RTO Compliance Tracking</h1>
          <p className={styles.heroSub}>
            Monitor recovery point and recovery time objectives against actual drill results
            across all products and environments.
          </p>
        </div>
        <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
          <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── Summary cards ── */}
      {!loading && !error && (
        <div className={styles.summaryRow}>
          <SummaryCard
            icon={BarChart3}
            label="Total Drills Measured"
            value={totalDrills}
            variant="neutral"
          />
          <SummaryCard
            icon={Clock}
            label="RPO Compliant"
            value={rpoCompliant}
            sub={`${rpcPct}% of drills`}
            variant={rpcPct >= 80 ? 'ok' : rpcPct >= 60 ? 'warn' : 'bad'}
          />
          <SummaryCard
            icon={TrendingUp}
            label="RTO Compliant"
            value={rtoCompliant}
            sub={`${rtcPct}% of drills`}
            variant={rtcPct >= 80 ? 'ok' : rtcPct >= 60 ? 'warn' : 'bad'}
          />
          <SummaryCard
            icon={ShieldCheck}
            label="Both Compliant"
            value={bothCompliant}
            sub={`${bothPct}% fully compliant`}
            variant={bothPct >= 80 ? 'ok' : bothPct >= 60 ? 'warn' : 'bad'}
          />
        </div>
      )}

      {/* ── Table section ── */}
      <div className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <TrendingUp size={15} />
            <h2>Compliance Records</h2>
            <span className={styles.countBadge}>{records.length} records</span>
          </div>
          <div className={styles.searchWrap}>
            <Search size={13} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Filter by product or drill…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch('')}>✕</button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={18} className={styles.spin} />
            <span>Loading compliance data…</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && records.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><TrendingUp size={32} /></div>
            <h3>No RPO/RTO records yet</h3>
            <p>
              Complete a DR drill with actual RPO/RTO measurements to see compliance data here.
            </p>
          </div>
        )}

        {/* No match */}
        {!loading && records.length > 0 && filtered.length === 0 && (
          <div className={styles.noMatch}>
            No records match <strong>"{search}"</strong>.{' '}
            <button onClick={() => setSearch('')}>Clear</button>
          </div>
        )}

        {/* Table */}
        {!loading && filtered.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Drill</th>
                  <th>Target RPO</th>
                  <th>Actual RPO</th>
                  <th>RPO Status</th>
                  <th>Target RTO</th>
                  <th>Actual RTO</th>
                  <th>RTO Status</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td>
                      <span className={styles.productName}>{r.product_name}</span>
                    </td>
                    <td>
                      <span className={styles.drillTitle}>{r.drill_title}</span>
                    </td>
                    <td className={styles.numCell}>{fmtHours(r.target_rpo_hours)}</td>
                    <td className={styles.numCell}>
                      <span className={r.rpo_met ? styles.numOk : styles.numBad}>
                        {fmtHours(r.actual_rpo_hours)}
                      </span>
                    </td>
                    <td><MetBadge met={r.rpo_met} /></td>
                    <td className={styles.numCell}>{fmtHours(r.target_rto_hours)}</td>
                    <td className={styles.numCell}>
                      <span className={r.rto_met ? styles.numOk : styles.numBad}>
                        {fmtHours(r.actual_rto_hours)}
                      </span>
                    </td>
                    <td><MetBadge met={r.rto_met} /></td>
                    <td className={styles.dateCell}>{fmtDate(r.recorded_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Products reference ── */}
      {!loading && products.length > 0 && (
        <div className={styles.productsSection}>
          <div className={styles.productsSectionHeader}>
            <ShieldCheck size={14} />
            <h2>Product RPO/RTO Targets</h2>
          </div>
          <div className={styles.productsGrid}>
            {products.map(p => (
              <div key={p.id} className={styles.productCard}>
                <div className={styles.productCardName}>{p.product_name}</div>
                <div className={styles.productCardTargets}>
                  <div className={styles.targetItem}>
                    <span className={styles.targetLabel}>RPO Target</span>
                    <span className={styles.targetValue}>
                      {parseFloat(p.rpo_target_hours)}h
                    </span>
                  </div>
                  <div className={styles.targetDivider} />
                  <div className={styles.targetItem}>
                    <span className={styles.targetLabel}>RTO Target</span>
                    <span className={styles.targetValue}>
                      {parseFloat(p.rto_target_hours)}h
                    </span>
                  </div>
                </div>
                <div className={styles.critChip} data-crit={p.criticality}>
                  {p.criticality}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
